
import { formatDistanceToNow } from "date-fns";
import { Campaign } from "@/hooks/useCampaigns";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CampaignActions } from "./CampaignActions";
import { TicketIcon, TicketPercentIcon } from "lucide-react";
import { formatDate } from "@/utils/voucherUtils";

interface CampaignsTableProps {
  campaigns: Campaign[];
  isLoading: boolean;
  searchQuery: string;
  onPublish: (campaign: Campaign) => void;
  onDelete: (campaign: Campaign) => void;
  onDuplicate: (campaign: Campaign) => void;
}

export function CampaignsTable({
  campaigns,
  isLoading,
  searchQuery,
  onPublish,
  onDelete,
  onDuplicate
}: CampaignsTableProps) {
  const filteredCampaigns = campaigns.filter(campaign => 
    campaign.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    campaign.description.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const statusColor = {
    active: "bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400",
    scheduled: "bg-blue-100 text-blue-800 dark:bg-blue-800/20 dark:text-blue-400",
    ended: "bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-400",
    draft: "bg-amber-100 text-amber-800 dark:bg-amber-800/20 dark:text-amber-400",
  };
  
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Campaign</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Vouchers</TableHead>
            <TableHead>Start Date</TableHead>
            <TableHead>End Date</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                Loading campaigns...
              </TableCell>
            </TableRow>
          ) : filteredCampaigns.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                {searchQuery ? "No campaigns match your search criteria" : "No campaigns available"}
              </TableCell>
            </TableRow>
          ) : (
            filteredCampaigns.map((campaign) => (
              <TableRow key={campaign.id}>
                <TableCell>
                  <div>
                    <div className="font-medium">{campaign.title}</div>
                    <div className="text-sm text-muted-foreground line-clamp-1">
                      {campaign.description}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    {campaign.campaign_type === "unique" ? (
                      <div className="flex items-center text-blue-600">
                        <TicketIcon className="h-4 w-4 mr-1" />
                        <span>Unique</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-purple-600">
                        <TicketPercentIcon className="h-4 w-4 mr-1" />
                        <span>Common</span>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={statusColor[campaign.status]}>
                    {campaign.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {campaign.vouchers_claimed} / {campaign.vouchers_total}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {formatDate(campaign.start_date)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div>{formatDate(campaign.end_date)}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(campaign.end_date), { addSuffix: true })}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <CampaignActions 
                    campaign={campaign}
                    onPublish={onPublish}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
